import { useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/utils/cn';

const SELECTION_BURST_PARTICLE_COUNT = 7;
const SELECTION_BURST_RESET_MS = 620;

/**
 * Paleta de partículas del burst. El color sigue al color del control:
 * `primary` (morado, por defecto), `cyan` (control cyan) y `muted` (control
 * apagado/desmarcado del course-card).
 */
export type SelectionBurstVariant = 'primary' | 'cyan' | 'muted';

/**
 * Estado de la micro-interacción de selección (burst de partículas) compartida
 * por todos los checkbox. La animación es la misma; el color del control es
 * independiente de esta animación.
 */
export function useSelectionBurst() {
  const [burstKey, setBurstKey] = useState(0);
  const [variant, setVariant] = useState<SelectionBurstVariant>('primary');
  const resetTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
    },
    [],
  );

  const triggerBurst = (nextVariant: SelectionBurstVariant = 'primary') => {
    setVariant(nextVariant);
    setBurstKey((currentKey) => currentKey + 1);

    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setBurstKey(0);
      resetTimer.current = null;
    }, SELECTION_BURST_RESET_MS);
  };

  return { burstKey, variant, triggerBurst };
}

/**
 * Render del burst. Posicionado en el centro del control contenedor (debe ser
 * `relative`). No captura eventos y es puramente decorativo.
 */
export function SelectionBurst({
  burstKey,
  variant,
  className,
}: {
  burstKey: number;
  variant: SelectionBurstVariant;
  className?: string;
}) {
  if (burstKey <= 0) return null;

  return (
    <span
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0', className)}
      key={burstKey}
    >
      {Array.from({ length: SELECTION_BURST_PARTICLE_COUNT }, (_, index) => (
        <span
          className={cn(
            'favorite-burst-particle',
            variant === 'primary' && 'favorite-burst-particle-primary',
            variant === 'cyan' && 'favorite-burst-particle-cyan',
            variant === 'muted' && 'favorite-burst-particle-muted',
          )}
          key={index}
        />
      ))}
    </span>
  );
}
